import * as R from 'ramda';
import currentUser from 'system/currentUser';
import getBase58Id from 'system/libs/getBase58Id';
import removeHTML from 'system/libs/removeHTML';
import radar from 'system/radar';
import { DPP } from 'utilities/DPP/DPP';
import { LogUtils } from 'utils/logUtils';
import { SentryUtils } from 'utils/SentryUtils';
import uuidv4 from 'uuid/dist/v4';

import xPageId from 'xPageId';
import xTabId from 'xTabId';

const SDKLogger = {
    createSingleRequestLogger,
};

export { SDKLogger };
function createSingleRequestLogger({
    group,
    logResponseTextOnError,
    type,
    method,
    backendType,
}) {
    const startTime = LogUtils.getPerformanceNow();

    const loggingHeaders = {
        [LogUtils.X_REQ_ID_HEADER]: getBase58Id(8),
        [LogUtils.X_MCS_REQUEST_ID]: uuidv4().replace(/-/g, ''),
        'x-email': currentUser.get('email'),
        'x-tab-id': xTabId,
        'x-page-id': xPageId,
        [LogUtils.X_BACKEND_TYPE]: backendType,
    };

    return {
        getLoggingHeaders() {
            return loggingHeaders;
        },

        logXhrOnResponse({ xhr, customData = {} }) {
            this.logOnResponse({
                status: xhr.status,
                responseText: xhr.responseText,

                getResponseHeader: (headerName) => {
                    let header = xhr.getResponseHeader(headerName);

                    if (header == null) {
                        header = _getHeaderCaseInsensitive(headerName, xhr);
                    }

                    return header;
                },

                customData: {
                    ...customData,
                    readyState: xhr.readyState,
                },
            });
        },

        logOnResponse({ status, responseText, getResponseHeader, customData }) {
            _log({
                group,
                type,
                method,
                loggingHeaders,
                startTime,
                endTime: LogUtils.getPerformanceNow(),
                status,
                logResponseTextOnError,
                responseText,
                getResponseHeader,
                customData,
            });
        },
    };
}

const _getHeaderCaseInsensitive = (headerName, xhr) => {
    const headerString = xhr.getAllResponseHeaders();

    const headers = R.mergeAll(
        headerString
            .split('\n')
            .map((i) => i.split(': '))
            .map(([key, value]) => ({
                [key.toLowerCase()]: value,
            })),
    );

    return headers[headerName.toLowerCase()];
};

function _log({
    group,
    type,
    method,
    loggingHeaders,
    startTime,
    endTime,
    status,
    logResponseTextOnError,
    responseText,
    getResponseHeader,
    customData = {},
}) {
    const xReqId = loggingHeaders[LogUtils.X_REQ_ID_HEADER];
    const requestDuration = Math.max(0, Math.round(endTime - startTime));

    const metrics = radar(group);

    const shortenedMethod = `${type}-${_shortenMethod(method)}`;

    metrics.add(shortenedMethod + '-cnt');

    metrics.add('http' + status);
    metrics.add(shortenedMethod + '-http' + status);

    metrics.add('dur', requestDuration);
    metrics.add(shortenedMethod + '-dur', requestDuration);
    metrics.param(LogUtils.X_REQ_ID_HEADER, xReqId);
    const logData = {
        shortenedMethod,
        type,
        method,
        status,
        duration: requestDuration,
        endTime: LogUtils.getPerformanceDateString(endTime),
        location: window.location.pathname,
        xReqId,
        serverDate: getResponseHeader('date'),
        'x-host': getResponseHeader('x-host'),
        ...loggingHeaders,
        ...customData,
    };

    // TODO: move this header recording to OpenstackRPC
    const OPENSTACK_REQ_ID_FIELD = 'x-openstack-request-id';

    const openStackReqId = getResponseHeader(OPENSTACK_REQ_ID_FIELD);

    if (openStackReqId) {
        logData[OPENSTACK_REQ_ID_FIELD] = openStackReqId;
    }

    if (status >= 400) {
        if (logResponseTextOnError) {
            logData.responseText = _getResponseText(responseText);
        }

        // eslint-disable-next-line no-console
        console[status >= 500 || status === 405 ? 'error' : 'warn'](
            'HTTP',
            status,
            method,
            LogUtils.X_REQ_ID_HEADER,
            xReqId,
            'ts',
            Math.round(Date.now() / 1000),
        );
    }

    DPP.sendEvent({
        type: 'request',
        payload: {
            group: group.replace('storage', 's3'),
            url: method, // e.g. /api/v1/projects/XXX/billing
            target: _shortenMethod(method),
            method: type,
            status,
            duration: requestDuration,
            reqId: xReqId,
            openStackReqId,
            mcsReqId: loggingHeaders[LogUtils.X_MCS_REQUEST_ID],
        },
    });

    SentryUtils.captureApiResponse(logData);
    metrics.kaktam('api', logData); // one file for all groups
    metrics.send();
}

const uuidRegex = /[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/g;
const mcsPidRegex = /\bmcs\d+/g;
const apiVersionRegex = /\/api\/v\d/;

const _shortenMethod = (method) =>
    method
        .replace(/\/infra\/identity\/.*\/auth$/, 'infra-identity-auth')
        .replace(
            /\/infra\/identity\/.*\/redirect\?logout.*/,
            'infra-identity-logout',
        )
        .replace(
            /\/infra\/container\/magnum\/v1\/secrets\/.*/,
            'infra-container-magnum-v1-secrets',
        )
        .replace(apiVersionRegex, '')
        .replace(uuidRegex, 'uuid')
        .replace(mcsPidRegex, 'pid')
        .replace('%projectId%', 'ospid')
        .replace(/\/\d+/g, '/id')
        .replace(/^\/infra\//, '')
        .replace(/^https:\/\//, '')
        .replace('mcs.st', '');

function _getResponseText(responseText) {
    responseText = String(responseText);

    if (responseText.length > 4096) {
        responseText = responseText.slice(0, 4096);
    }

    try {
        responseText = JSON.parse(responseText);
    } catch (reason) {
        responseText = removeHTML(responseText)
            .trim()
            .replace(/\s+/g, ' ')
            .replace(/\t+/g, '\t')
            .replace(/[\r\n]+/gm, '\n')
            .slice(0, 1024);
    }

    return responseText;
}
